import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import type { HealthCheck } from '../types/database';

// ---------------------------------------------------------------------------
// useHealthChecksCurrent — latest check per service, auto-refreshes
// ---------------------------------------------------------------------------

interface CurrentResult {
	/** Map of service_name -> latest HealthCheck */
	services: Map<string, HealthCheck>;
	loading: boolean;
	error: string | null;
	refetch: () => void;
}

/**
 * Fetches the most recent health check row per service.
 * Auto-refreshes every `refreshMs` (default 30 000 ms).
 */
export function useHealthChecksCurrent(refreshMs = 30_000): CurrentResult {
	const [services, setServices] = useState<Map<string, HealthCheck>>(new Map());
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [tick, setTick] = useState(0);

	const refetch = useCallback(() => setTick((t) => t + 1), []);

	// Auto-refresh timer
	useEffect(() => {
		const id = setInterval(refetch, refreshMs);
		return () => clearInterval(id);
	}, [refetch, refreshMs]);

	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		setError(null);

		// Fetch last 100 rows (covers 13+ services * several cycles) and
		// deduplicate client-side — keeps the query trivial.
		supabase
			.from('health_checks')
			.select('*')
			.order('checked_at', { ascending: false })
			.limit(100)
			.then(({ data, error: err }) => {
				if (cancelled) return;
				if (err) {
					setError(err.message);
					setServices(new Map());
				} else {
					const map = new Map<string, HealthCheck>();
					for (const row of (data ?? []) as HealthCheck[]) {
						if (!map.has(row.service_name)) {
							map.set(row.service_name, row);
						}
					}
					setServices(map);
				}
				setLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [tick]);

	return { services, loading, error, refetch };
}

// ---------------------------------------------------------------------------
// useHealthChecksHistory — incidents + stats within a time window
// ---------------------------------------------------------------------------

export interface ServiceStats {
	totalChecks: number;
	healthyChecks: number;
	uptimePct: number;
	avgLatencyMs: number | null;
}

interface HistoryResult {
	/** All non-healthy check rows in the time range (max 1000) */
	incidents: HealthCheck[];
	/** Per-service computed stats (uptime %, avg latency) */
	stats: Map<string, ServiceStats>;
	loading: boolean;
	error: string | null;
	refetch: () => void;
}

/**
 * Fetches health check summary data for a time window using multiple
 * targeted PostgREST queries in parallel:
 *
 * 1. **Incidents**: all non-healthy rows in the range (limit 1000)
 * 2. **Per-service total count**: HEAD-only request with exact count
 * 3. **Per-service healthy count**: HEAD-only request with exact count
 * 4. **Per-service latency sample**: last 20 healthy rows for avg latency
 *
 * This avoids the Supabase 1000-row default limit that previously caused
 * the timeline to only show partial data.
 */
export function useHealthChecksHistory(
	hours: number = 24,
	serviceNames: string[] = [],
): HistoryResult {
	const [incidents, setIncidents] = useState<HealthCheck[]>([]);
	const [stats, setStats] = useState<Map<string, ServiceStats>>(new Map());
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [tick, setTick] = useState(0);

	const hoursRef = useRef(hours);
	hoursRef.current = hours;
	const servicesRef = useRef(serviceNames);
	servicesRef.current = serviceNames;

	const refetch = useCallback(() => setTick((t) => t + 1), []);

	// Auto-refresh every 60 s
	useEffect(() => {
		const id = setInterval(refetch, 60_000);
		return () => clearInterval(id);
	}, [refetch]);

	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		setError(null);

		const since = new Date(
			Date.now() - hoursRef.current * 3600_000,
		).toISOString();
		const services = servicesRef.current;

		// ---- Build all queries in parallel ----

		// 1. Incidents: all non-healthy rows in the period
		const incidentsQuery = supabase
			.from('health_checks')
			.select('*')
			.neq('status', 'healthy')
			.gte('checked_at', since)
			.order('checked_at', { ascending: true })
			.limit(1000);

		// 2 + 3. Per-service count queries (total + healthy)
		const totalCountQueries = services.map((svc) =>
			supabase
				.from('health_checks')
				.select('id', { count: 'exact', head: true })
				.eq('service_name', svc)
				.gte('checked_at', since),
		);

		const healthyCountQueries = services.map((svc) =>
			supabase
				.from('health_checks')
				.select('id', { count: 'exact', head: true })
				.eq('service_name', svc)
				.eq('status', 'healthy')
				.gte('checked_at', since),
		);

		// 4. Per-service latency sample (last 20 healthy rows)
		const latencyQueries = services.map((svc) =>
			supabase
				.from('health_checks')
				.select('response_time_ms')
				.eq('service_name', svc)
				.eq('status', 'healthy')
				.gte('checked_at', since)
				.not('response_time_ms', 'is', null)
				.order('checked_at', { ascending: false })
				.limit(20),
		);

		// Fire all queries in parallel
		Promise.all([
			incidentsQuery,
			Promise.all(totalCountQueries),
			Promise.all(healthyCountQueries),
			Promise.all(latencyQueries),
		]).then(([incidentsRes, totalCounts, healthyCounts, latencySamples]) => {
			if (cancelled) return;

			// Check for errors
			const errors: string[] = [];
			if (incidentsRes.error) errors.push(incidentsRes.error.message);
			for (const r of totalCounts) {
				if (r.error) errors.push(r.error.message);
			}
			for (const r of healthyCounts) {
				if (r.error) errors.push(r.error.message);
			}
			for (const r of latencySamples) {
				if (r.error) errors.push(r.error.message);
			}

			if (errors.length > 0) {
				setError(errors[0]);
				setIncidents([]);
				setStats(new Map());
				setLoading(false);
				return;
			}

			// Parse incidents
			setIncidents((incidentsRes.data ?? []) as HealthCheck[]);

			// Build stats map
			const statsMap = new Map<string, ServiceStats>();
			for (let i = 0; i < services.length; i++) {
				const svc = services[i];
				const total = totalCounts[i].count ?? 0;
				const healthy = healthyCounts[i].count ?? 0;
				const uptimePct =
					total > 0
						? Math.round((healthy / total) * 1000) / 10
						: 0;

				// Compute avg latency from sample
				const latencyRows = (latencySamples[i].data ?? []) as {
					response_time_ms: number;
				}[];
				let avgLatencyMs: number | null = null;
				if (latencyRows.length > 0) {
					const sum = latencyRows.reduce(
						(acc, r) => acc + r.response_time_ms,
						0,
					);
					avgLatencyMs = Math.round(sum / latencyRows.length);
				}

				statsMap.set(svc, {
					totalChecks: total,
					healthyChecks: healthy,
					uptimePct,
					avgLatencyMs,
				});
			}
			setStats(statsMap);
			setLoading(false);
		});

		return () => {
			cancelled = true;
		};
	}, [tick, hours, serviceNames]);

	return { incidents, stats, loading, error, refetch };
}
